/**
 * useWeather - Client hook for fetching weather data.
 * Handles location retrieval, API calls, and caching.
 */

import { useCallback, useEffect, useState } from 'react';
import { locationService } from '@/lib/services/LocationService';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import type { WeatherForecast, WeatherResponse, WeatherError } from '@/types/weather';

type UseWeatherResult = {
  forecasts: WeatherForecast[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

type UseWeatherForDateResult = {
  forecast: WeatherForecast | null;
  isLoading: boolean;
  error: WeatherError | null;
};

export function useWeather(days: number = 7): UseWeatherResult {
  const [forecasts, setForecasts] = useState<WeatherForecast[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWeather = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const location = await locationService.getLocation();
      if (!location) {
        setError('Location unavailable');
        return;
      }

      const response = await authenticatedFetch.fetch(
        `/api/weather?lat=${location.latitude}&lon=${location.longitude}&days=${days}`,
      );

      if (!response.ok) throw new Error(`Weather fetch failed (${response.status})`);

      const data = (await response.json()) as WeatherResponse;
      setForecasts(data.forecasts);
    } catch (err) {
      console.error('[useWeather]', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  return { forecasts, isLoading, error, refresh: fetchWeather };
}

export function useWeatherForDate(date: string | null): UseWeatherForDateResult {
  const [forecast, setForecast] = useState<WeatherForecast | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<WeatherError | null>(null);

  useEffect(() => {
    if (!date) {
      setForecast(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchForecast = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const location = await locationService.getLocation();
        if (!location) {
          if (!cancelled) setError({ type: 'location', message: 'Turn on location settings' });
          return;
        }
        if (cancelled) return;

        const response = await authenticatedFetch.fetch(
          `/api/weather?lat=${location.latitude}&lon=${location.longitude}&date=${date}`,
        );

        if (!response.ok) {
          if (!cancelled) setError({ type: 'api', message: `error ${response.status}` });
          return;
        }
        if (cancelled) return;

        const data = (await response.json()) as { forecast: WeatherForecast | null };
        if (!cancelled) {
          setForecast(data.forecast);
          if (!data.forecast) {
            setError({ type: 'api', message: 'no forecast data' });
          }
        }
      } catch (err) {
        console.error('[useWeatherForDate]', err);
        if (!cancelled) setError({ type: 'network', message: 'network error' });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchForecast();
    return () => { cancelled = true; };
  }, [date]);

  return { forecast, isLoading, error };
}
